<template>
  <div id="app" class="app-container">
    <header class="app-header">
      <a href="#main" class="sr-only sr-only-focusable" >Skip to main content</a>
      <AppNavBar />
      <!-- <div v-show="showSessionReminder">
        <div class="border rounded p-2 bg-warning d-flex justify-content-center align-items-center" >
          <div>
            <strong>Session Timeout: </strong><span class="mx-2">Your session will timout in</span>      
          </div>
          <strong><Vidle @idle="onIdle"
                         @remind="onRemind"
                         @refresh="onRefresh"
                         :duration=timeoutDuration
                         :reminders=reminders /> 
          </strong>                     
        </div>        
      </div> -->
    </header>
    <main id="main" role="main" class="app-content">
      <Toast group="top-toast" position="top-center"/>
      <MnConfirmDialog />
      <MnAlertDialog />
      <RouterView />

    </main>
    <Teleport to="body">
      <MnLoader :visible="loadingState.loading" :message="loadingState.loadingMessage" />
      <div v-if="showIdleWarning" 
           class="idle-overlay k-bg-warning" 
           role="dialog" 
           aria-modal="true" 
           aria-labelledby="idle-warning-title"
           aria-describedby="idle-warning-desc">
        <div class="idle-warning">
          <div class="k-mb-3">
            <h2 id="idle-warning-title" class="k-font-weight-semibold k-mb-2">Idle Warning</h2>
            <p id="idle-warning-desc" class="k-font-size-sm">{{ idleTimeout.timeoutMessage.value }}</p>
          </div>
          <ProgressBar :value="idleTimeout.timePercentage.value">{{ idleTimeout.timeRemaining.value }}s</ProgressBar>
        </div>
      </div>
    </Teleport>           
  </div>
</template>

<script setup>
// ===============================
// IMPORTS
// ===============================
// Vue Core
import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import { RouterView } from 'vue-router';


// Components
import AppNavBar from '@/components/menu/AppNavBar.vue';
import Toast from 'primevue/toast';
import MnAlertDialog from './components/common/MnAlertDialog.vue';
import MnLoader from './components/common/MnLoader.vue';
import MnConfirmDialog from './components/common/MnConfirmDialog.vue';
import ProgressBar from 'primevue/progressbar';


// Composables
import { useIdleTimeout } from "@/composables/IdleTimeout";

// Stores
import { useAuthStore } from './stores/authStore';
import { useAppStore } from './stores/appStore';

// Constants and Utils


// ===============================
// PROPS & EMITS
// ===============================


// ===============================
// COMPOSABLES & STORES
// ===============================
const authStore = useAuthStore();
const appStore = useAppStore();
const { loadingState } = appStore;
const idleTimeout = useIdleTimeout();

// ===============================
// REACTIVE STATE
// ===============================


// ===============================
// CONSTANTS
// ===============================
const timeoutDuration =  parseInt(import.meta.env.VITE_APP_TIMEOUT);
const showSessionReminder = ref(false);
let tokenRefreshInterval = null;


// ===============================
// COMPUTED PROPERTIES
// ===============================
const showIdleWarning = computed(() => {
  return idleTimeout.idle.value && authStore.authenticated;
});

// ===============================
// METHODS
// ===============================

const startTokenRefreshInterval = () => {
  // Clear any existing interval
  stopTokenRefreshInterval();

  const refreshInterval = (timeoutDuration - 60) * 1000;
  // Refresh token every 4 minutes (240 seconds)
  // This is 1 minute before the 5-minute token expiration
  tokenRefreshInterval = setInterval(async () => {
    if (authStore.authenticated && !showSessionReminder.value && authStore.isTokenExpired) {
      try {
        await authStore.refreshUserToken();
      } catch (error) {
        console.error('Auto token refresh failed:', error);
        // Let the idle timeout handle logout
      }
    }
  }, refreshInterval);
};

const stopTokenRefreshInterval = () => {
  if (tokenRefreshInterval) {
    clearInterval(tokenRefreshInterval);
    tokenRefreshInterval = null;
  }
};

// ===============================
// WATCHERS
// ===============================
watch(() => idleTimeout.isTimedOut.value, (isTimedOut) => {
  if (isTimedOut && authStore.authenticated) {
    authStore.logout();
  }
});

// ===============================
// LIFECYCLE HOOKS
// ===============================
onMounted(() => {
  watch(
    () => authStore.authenticated,
    async (authenticated) => {
      if (authenticated) {
        try {
          await appStore.initializeStore();
          // Start proactive token refresh
          startTokenRefreshInterval();
        } catch (error) {
          console.error('Error initializing app store:', error);
        }
      } else {
        // Stop proactive token refresh when not authenticated
        stopTokenRefreshInterval();
      }
    },
    { immediate: true } // Triggers the watcher immediately upon setup
  );

});

onUnmounted(() => {
  stopTokenRefreshInterval();
});

// ===============================
// EXPOSE
// ===============================

</script>
<style>
.app-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.app-header {
  flex-shrink: 0;
}

.app-content {
  padding: 1rem;
  flex-grow: 1;
  overflow-y: auto;
}

/* Idle overlay styles */
.idle-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 21000;
}
.idle-warning {
  background: var(--kendo-warning, #fcebbd);
  color: var(--kendo-body-text, #111);
  padding: 1.25rem 1.5rem;
  border-radius: 8px;
  min-width: 150px;
  max-width: 400px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.35);
  text-align: center;
  pointer-events: auto;
}
</style>



